import { useState, useCallback } from 'react';
import { useRoomStore } from '../roomStore';

/**
 * Convenience hook that composes room state + actions with local UI state
 * (loading, error feedback for the join form).
 */
export function useRoom() {
  const store = useRoomStore();
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const createAndJoin = useCallback(
    async (roomName: string) => {
      setIsJoining(true);
      setJoinError(null);
      try {
        await store.createAndJoin(roomName);
      } catch (err) {
        setJoinError(extractErrorMessage(err));
      } finally {
        setIsJoining(false);
      }
    },
    [store],
  );

  const joinByLink = useCallback(
    async (link: string) => {
      setIsJoining(true);
      setJoinError(null);
      try {
        await store.joinByLink(link);
      } catch (err) {
        setJoinError(extractErrorMessage(err));
      } finally {
        setIsJoining(false);
      }
    },
    [store],
  );

  return {
    ...store,
    isJoining,
    joinError,
    createAndJoin,
    joinByLink,
  };
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message);
  }
  return 'Failed to join room';
}